import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageSquare, Send, Search, User, Sprout, Factory, Shield, CheckCheck, Circle, Users, PlusCircle, X, MapPin, Building } from 'lucide-react';
import { io } from 'socket.io-client';
import { messagesAPI, farmersAPI, processorsAPI } from '../services/api';
import { normalizeFarmers, normalizeProcessors } from '../utils/dataTransform';

const SOCKET_SERVER_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3000').replace(/\/+$/, '');

export function MessagesPage({ user }) {
  const [searchParams] = useSearchParams();
  const partnerIdFromQuery = searchParams.get('partnerId');
  const partnerNameFromQuery = searchParams.get('partnerName');
  const partnerRoleFromQuery = searchParams.get('partnerRole');

  const [sidebarTab, setSidebarTab] = useState('chats'); // 'chats' | 'directory'
  const [directoryRoleFilter, setDirectoryRoleFilter] = useState('all'); // 'all' | 'farmer' | 'processor' | 'admin'
  const [conversations, setConversations] = useState([]);
  const [activePartner, setActivePartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [directoryList, setDirectoryList] = useState([]);
  const [loadingDirectory, setLoadingDirectory] = useState(false);
  const [sending, setSending] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom of message thread
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Setup Socket.IO connection for real-time messaging
  useEffect(() => {
    if (!user) return;
    const userId = user.id || user.username;

    const socket = io(SOCKET_SERVER_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join', userId);
    });

    socket.on('new_message', (newMsg) => {
      // Refresh conversations list
      messagesAPI.getConversations(userId).then(res => {
        if (res.success) setConversations(res.conversations || []);
      });

      // If this message belongs to the current thread, append it
      setActivePartner(currentPartner => {
        if (currentPartner) {
          const stripPrefix = id => String(id || '').replace(/^[a-z]+_/i, '');
          const pId = stripPrefix(currentPartner.partnerId);
          const sId = stripPrefix(newMsg.senderId);
          const rId = stripPrefix(newMsg.receiverId);
          const uId = stripPrefix(userId);

          const isCurrentThread = (sId === pId && rId === uId) || (sId === uId && rId === pId);
          if (isCurrentThread) {
            setMessages(prev => {
              if (prev.some(m => String(m.id) === String(newMsg.id))) return prev;
              return [...prev, newMsg];
            });
          }
        }
        return currentPartner;
      });
    });

    return () => {
      socket.disconnect();
    };
  }, [user]);

  // Load conversations & directory partners directly from users table
  useEffect(() => {
    const loadData = async () => {
      if (!user) return;
      setLoadingDirectory(true);
      try {
        const currentUserId = user.id || user.username || user.profileId;
        const [convRes, dirRes] = await Promise.all([
          messagesAPI.getConversations(currentUserId).catch(() => ({ success: false, conversations: [] })),
          messagesAPI.getDirectory().catch(() => ({ success: false, directory: [] }))
        ]);

        if (convRes.success) {
          setConversations(convRes.conversations || []);
        }

        const rawDirectory = dirRes.directory || dirRes.data || (Array.isArray(dirRes) ? dirRes : []);
        const usersList = rawDirectory.map(u => {
          const fName = (u.firstName || '').trim();
          const lName = (u.lastName || '').trim();
          const cleanFullName = [fName, lName].filter(Boolean).join(' ') || u.username || `User #${u.id}`;
          return {
            id: String(u.id),
            rawId: String(u.id),
            username: u.username || '',
            name: cleanFullName,
            rawName: cleanFullName,
            firstName: fName,
            lastName: lName,
            role: u.role || 'user',
            location: u.municipality || u.organization || 'Bohol',
            municipality: u.municipality || '',
            organization: u.organization || '',
            email: u.email || ''
          };
        });

        // Ensure Admin support contact is available if current user is not Admin
        const hasAdmin = usersList.some(u => u.role === 'admin');
        const allPartners = [...usersList];
        if (!hasAdmin && user.role !== 'admin') {
          allPartners.unshift({
            id: 'admin',
            rawId: 'admin',
            username: 'admin',
            name: 'BCIIS Administrator Support',
            rawName: 'Administrator Support',
            firstName: 'Administrator',
            lastName: 'Support',
            role: 'admin',
            location: 'Office of the Provincial Agriculturist / BISU',
            municipality: 'Tagbilaran City',
            organization: 'Provincial Government of Bohol',
            email: 'admin@bciis.ph'
          });
        }

        // Filter out the logged-in user themselves
        const currentIdStr = String(user.id || user.username || '').toLowerCase();
        const currentUsernameStr = String(user.username || '').toLowerCase();

        const filteredList = allPartners.filter(p => {
          const pId = String(p.id).toLowerCase();
          const pUsername = String(p.username || '').toLowerCase();
          if (pId === currentIdStr || pUsername === currentIdStr || (currentUsernameStr && pUsername === currentUsernameStr)) {
            return false;
          }
          return true;
        });

        setDirectoryList(filteredList);
      } catch (err) {
        console.error('Error loading directory from users table:', err);
      } finally {
        setLoadingDirectory(false);
      }
    };

    loadData();
  }, [user]);

  // Handle partner from query param
  useEffect(() => {
    if (partnerIdFromQuery) {
      const partner = {
        partnerId: partnerIdFromQuery,
        partnerName: partnerNameFromQuery || 'Industry Partner',
        partnerRole: partnerRoleFromQuery || 'user'
      };
      setActivePartner(partner);
    }
  }, [partnerIdFromQuery, partnerNameFromQuery, partnerRoleFromQuery]);

  // Load active chat thread
  useEffect(() => {
    if (!user || !activePartner) return;
    const fetchThread = async () => {
      try {
        const currentUserId = user.id || user.username;
        const res = await messagesAPI.getThread(currentUserId, activePartner.partnerId);
        if (res.success) {
          setMessages(res.thread || []);
        }
      } catch (err) {
        // ignore
      }
    };

    fetchThread();
    const interval = setInterval(fetchThread, 8000); // Polling backup interval
    return () => clearInterval(interval);
  }, [user, activePartner]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !activePartner || !user || sending) return;

    setSending(true);
    const msgText = inputMessage.trim();
    setInputMessage('');

    try {
      const res = await messagesAPI.sendMessage({
        senderId: user.id || user.username,
        senderName: user.fullname || user.fullName || user.username || 'User',
        senderRole: user.role || 'user',
        receiverId: activePartner.partnerId,
        receiverName: activePartner.partnerName,
        receiverRole: activePartner.partnerRole,
        message: msgText
      });

      if (res.success && res.message) {
        setMessages(prev => {
          if (prev.some(m => String(m.id) === String(res.message.id))) return prev;
          return [...prev, res.message];
        });
        // Refresh conversation list
        const convRes = await messagesAPI.getConversations(user.id || user.username);
        if (convRes.success) setConversations(convRes.conversations || []);
      }
    } catch (err) {
      alert('Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'farmer':
        return <Sprout className="w-4 h-4 text-emerald-600" />;
      case 'processor':
        return <Factory className="w-4 h-4 text-amber-600" />;
      case 'admin':
        return <Shield className="w-4 h-4 text-blue-600" />;
      default:
        return <User className="w-4 h-4 text-gray-600" />;
    }
  };

  // Comprehensive Search Filter: First Name, Last Name, Full Name, Business, Location, Role
  const filteredDirectory = useMemo(() => {
    let list = directoryList;

    if (directoryRoleFilter !== 'all') {
      list = list.filter(p => p.role === directoryRoleFilter);
    }

    if (!searchQuery.trim()) return list;

    // Split search query into tokens so "Aquilio Daeng" or "Daeng Aquilio" matches regardless of order
    const tokens = searchQuery.toLowerCase().trim().split(/\s+/).filter(Boolean);

    return list.filter(p => {
      const searchableStrings = [
        p.name || '',
        p.rawName || '',
        p.firstName || '',
        p.lastName || '',
        p.businessName || '',
        p.ownerName || '',
        p.location || '',
        p.municipality || '',
        p.barangay || '',
        p.role || ''
      ].map(s => s.toLowerCase());

      const unifiedText = searchableStrings.join(' ');

      // Every word typed must appear somewhere in the contact's details
      return tokens.every(token => unifiedText.includes(token));
    });
  }, [directoryList, searchQuery, directoryRoleFilter]);

  const isSearching = searchQuery.trim().length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-green-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="mb-6 bg-gradient-to-r from-amber-700 via-amber-800 to-amber-900 rounded-2xl shadow-xl p-6 text-white flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 backdrop-blur-md p-3.5 rounded-2xl shadow-inner">
              <MessageSquare className="h-8 w-8 text-amber-200" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight">Direct Messaging Portal</h1>
              <p className="text-amber-100 text-sm mt-0.5">
                Connect directly with Farmers, Processors, and Admin across Bohol's Cacao Industry Network.
              </p>
            </div>
          </div>
        </div>

        {/* Chat Layout Container */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden flex flex-col lg:grid lg:grid-cols-12 h-[78vh] min-h-[550px]">
          
          {/* Sidebar Conversations & Directory (4 cols) */}
          <div className="lg:col-span-4 border-r border-gray-100 flex flex-col bg-gray-50/50 min-h-0 h-full">
            
            {/* Search Bar */}
            <div className="p-3.5 bg-white border-b border-gray-100 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by first name, last name, business, municipality..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-8 py-2.5 bg-gray-100/80 border border-gray-200 rounded-xl text-xs focus:bg-white focus:ring-2 focus:ring-amber-500 outline-none transition"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Role filter pills when in Directory tab or searching */}
              {(sidebarTab === 'directory' || isSearching) && (
                <div className="flex gap-1 mt-2.5 overflow-x-auto pb-0.5">
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'farmer', label: 'Farmers' },
                    { key: 'processor', label: 'Processors' },
                    ...(user.role !== 'admin' ? [{ key: 'admin', label: 'Admin' }] : [])
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setDirectoryRoleFilter(tab.key)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition flex-shrink-0 ${
                        directoryRoleFilter === tab.key
                          ? 'bg-amber-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar View Tabs (Chats vs Directory) */}
            {!isSearching && (
              <div className="flex border-b border-gray-200/80 bg-gray-100/60 p-1.5 gap-1.5 flex-shrink-0">
                <button
                  onClick={() => setSidebarTab('chats')}
                  className={`flex-1 py-1.5 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 ${
                    sidebarTab === 'chats'
                      ? 'bg-white text-amber-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  }`}
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  Chats ({conversations.length})
                </button>
                <button
                  onClick={() => setSidebarTab('directory')}
                  className={`flex-1 py-1.5 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 ${
                    sidebarTab === 'directory'
                      ? 'bg-white text-amber-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  }`}
                >
                  <Users className="w-3.5 h-3.5" />
                  Directory ({directoryList.length})
                </button>
              </div>
            )}

            {/* Conversation List & Partner Search */}
            <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-gray-100">
              {isSearching ? (
                // Live Search Results
                <div className="p-3">
                  <div className="flex items-center justify-between mb-2 px-1">
                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                      Search Results ({filteredDirectory.length})
                    </p>
                    <span className="text-[10px] text-gray-500">Matching first/last name, location</span>
                  </div>
                  {filteredDirectory.length === 0 ? (
                    <div className="p-6 text-center text-gray-400">
                      <p className="text-xs font-medium">No partner found matching "{searchQuery}"</p>
                      <p className="text-[11px] text-gray-400 mt-1">Try searching by first name, last name, business name, or municipality.</p>
                    </div>
                  ) : (
                    filteredDirectory.map(p => (
                      <div
                        key={p.id}
                        onClick={() => {
                          setActivePartner({ partnerId: p.id, partnerName: p.name, partnerRole: p.role });
                          setSearchQuery('');
                          setSidebarTab('chats');
                        }}
                        className={`p-3 rounded-2xl hover:bg-amber-50 cursor-pointer transition flex items-center gap-3 mb-1.5 border ${
                          activePartner?.partnerId === p.id ? 'bg-amber-100/70 border-amber-300' : 'bg-white border-gray-100 shadow-sm'
                        }`}
                      >
                        <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center text-amber-800 font-bold text-xs flex-shrink-0">
                          {getRoleIcon(p.role)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-bold text-gray-900 truncate">{p.name}</p>
                            <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                              {p.role}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-[11px] text-gray-500 truncate mt-0.5">
                            <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{p.location}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : sidebarTab === 'directory' ? (
                // Full Directory List
                <div className="p-3">
                  <div className="flex items-center justify-between mb-2 px-1">
                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Start New Chat</p>
                    <span className="text-[11px] text-amber-800 font-semibold">{filteredDirectory.length} contacts</span>
                  </div>
                  {loadingDirectory ? (
                    <div className="p-6 text-center text-gray-400 text-xs">Loading directory partners...</div>
                  ) : filteredDirectory.length === 0 ? (
                    <div className="p-6 text-center text-gray-400 text-xs">No directory partners found.</div>
                  ) : (
                    filteredDirectory.map(p => (
                      <div
                        key={p.id}
                        onClick={() => {
                          setActivePartner({ partnerId: p.id, partnerName: p.name, partnerRole: p.role });
                          setSidebarTab('chats');
                        }}
                        className={`p-3 rounded-2xl hover:bg-amber-50 cursor-pointer transition flex items-center gap-3 mb-1.5 border ${
                          activePartner?.partnerId === p.id ? 'bg-amber-100/70 border-amber-300' : 'bg-white border-gray-100 shadow-sm'
                        }`}
                      >
                        <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center text-amber-800 font-bold text-xs flex-shrink-0">
                          {getRoleIcon(p.role)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-bold text-gray-900 truncate">{p.name}</p>
                            <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                              {p.role}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-[11px] text-gray-500 truncate mt-0.5">
                            <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{p.location}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                // Existing Conversations View
                conversations.length === 0 ? (
                  <div className="p-8 text-center text-gray-400 flex flex-col items-center">
                    <MessageSquare className="w-10 h-10 text-gray-300 mb-2" />
                    <p className="text-xs font-semibold text-gray-600">No active conversations</p>
                    <p className="text-[11px] text-gray-400 mt-1 max-w-xs">
                      Click the "Directory" tab above or search by name to start a new chat.
                    </p>
                    <button
                      onClick={() => setSidebarTab('directory')}
                      className="mt-3 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 transition"
                    >
                      <PlusCircle className="w-3.5 h-3.5" /> Start a Conversation
                    </button>
                  </div>
                ) : (
                  conversations.map(c => (
                    <div
                      key={c.partnerId}
                      onClick={() => setActivePartner({ partnerId: c.partnerId, partnerName: c.partnerName, partnerRole: c.partnerRole })}
                      className={`p-3.5 flex items-center gap-3 cursor-pointer transition ${
                        activePartner?.partnerId === c.partnerId ? 'bg-amber-100/60 border-l-4 border-amber-600' : 'hover:bg-gray-100/70 bg-white'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-2xl bg-amber-100 flex items-center justify-center text-amber-800 font-bold text-xs flex-shrink-0 shadow-inner">
                        {getRoleIcon(c.partnerRole)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-bold text-gray-900 truncate">{c.partnerName}</p>
                          {c.lastMessageTime && (
                            <span className="text-[10px] text-gray-400">
                              {new Date(c.lastMessageTime).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500 truncate mt-0.5">{c.lastMessage}</p>
                      </div>
                      {c.unread && (
                        <span className="w-2.5 h-2.5 rounded-full bg-amber-600 flex-shrink-0"></span>
                      )}
                    </div>
                  ))
                )
              )}
            </div>
          </div>

          {/* Active Chat Main Panel (8 cols) */}
          <div className="lg:col-span-8 flex flex-col bg-white min-h-0 h-full">
            {activePartner ? (
              <>
                {/* Chat Panel Header */}
                <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50 flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-amber-100 flex items-center justify-center text-amber-800 font-bold text-sm shadow-inner">
                      {getRoleIcon(activePartner.partnerRole)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-sm font-bold text-gray-900">{activePartner.partnerName}</h2>
                        <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-200/60 text-amber-900">
                          {activePartner.partnerRole}
                        </span>
                      </div>
                      <p className="text-[11px] text-emerald-600 flex items-center gap-1 mt-0.5">
                        <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" /> Active Industry Partner
                      </p>
                    </div>
                  </div>
                </div>

                {/* Chat Thread Messages Area */}
                <div className="flex-1 min-h-0 p-4 overflow-y-auto space-y-3 bg-gray-50/30">
                  {messages.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">
                      <MessageSquare className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      <p className="text-xs font-semibold text-gray-600">Start of conversation</p>
                      <p className="text-[11px] mt-0.5 text-gray-400">Send a message to initiate communication with {activePartner.partnerName}.</p>
                    </div>
                  ) : (
                    messages.map((m, idx) => {
                      const myId = String(user.id || user.username || '');
                      const myPlain = myId.replace(/^[a-z]+_/i, '');
                      const sId = String(m.senderId || '');
                      const sPlain = sId.replace(/^[a-z]+_/i, '');
                      const isMe = sId === myId || (myPlain && sPlain === myPlain);

                      return (
                        <div
                          key={m.id || idx}
                          className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
                        >
                          <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-xs shadow-sm leading-relaxed ${
                            isMe
                              ? 'bg-gradient-to-r from-amber-700 to-amber-800 text-white rounded-br-none'
                              : 'bg-white border border-gray-200/80 text-gray-800 rounded-bl-none'
                          }`}>
                            <p>{m.message}</p>
                          </div>
                          <span className="text-[10px] text-gray-400 mt-1 px-1 flex items-center gap-1">
                            {m.timestamp ? new Date(m.timestamp).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' }) : ''}
                            {isMe && <CheckCheck className="w-3 h-3 text-amber-600" />}
                          </span>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Text Form */}
                <form onSubmit={handleSendMessage} className="p-3 border-t border-gray-100 bg-white flex items-center gap-2">
                  <input
                    type="text"
                    placeholder={`Message ${activePartner.partnerName}...`}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    disabled={sending}
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-2xl text-xs focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition disabled:opacity-50"
                  />
                  <button
                    type="submit"
                    disabled={sending || !inputMessage.trim()}
                    className="p-3 bg-gradient-to-r from-amber-700 to-amber-800 hover:from-amber-800 hover:to-amber-900 text-white rounded-2xl shadow transition disabled:opacity-40"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              </>
            ) : (
              // Empty Select State
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-gray-400">
                <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-3">
                  <MessageSquare className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-sm font-bold text-gray-800">Select a Conversation</h3>
                <p className="text-xs text-gray-500 mt-1 max-w-sm">
                  Choose a conversation from the sidebar or search for a registered farmer, processor, or admin to start messaging.
                </p>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
